import { useState, useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  TextField,
} from "@mui/material";
import { ChevronDown, Search } from "../../../Components/ui/icons";
import { Layer, mockLayers } from "../../../global/constants/layers";
import Typography from "../../../Components/Typography";
import { Button } from "../../../Components/ui/buttons/button";
import StyledCheckbox from "../../../Components/ui/styled/styled-checkbox";
import { Field, FieldGroup } from "../../../Components/ui/shadcn/forms/field";
import { Alert } from "../../../Components/ui/shadcn/alert";

interface LayerChecklistProps {
  selectedLayers: string[];
  onSave: (layers: string[]) => Promise<void>;
  loading?: boolean;
}

export default function LayerChecklist({
  selectedLayers: initialSelected,
  onSave,
  loading = false,
}: LayerChecklistProps) {
  const [selectedLayers, setSelectedLayers] =
    useState<string[]>(initialSelected);
  const [searchTerm, setSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Update when initial selection changes
  useEffect(() => {
    setSelectedLayers(initialSelected);
  }, [initialSelected]);

  // Group layers by category
  const layersByCategory = useMemo(() => {
    const grouped: { [category: string]: Layer[] } = {};
    mockLayers.forEach((layer) => {
      if (!grouped[layer.category]) {
        grouped[layer.category] = [];
      }
      grouped[layer.category].push(layer);
    });
    return grouped;
  }, []);

  // Filter layers based on search
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return layersByCategory;

    const lowerSearch = searchTerm.toLowerCase();
    const filtered: { [category: string]: Layer[] } = {};

    Object.entries(layersByCategory).forEach(([category, layers]) => {
      const matchingLayers = layers.filter((layer) =>
        layer.title.toLowerCase().includes(lowerSearch)
      );
      if (matchingLayers.length > 0) {
        filtered[category] = matchingLayers;
      }
    });

    return filtered;
  }, [layersByCategory, searchTerm]);

  const handleToggleLayer = (layerName: string) => {
    setSelectedLayers((prev) => {
      if (prev.includes(layerName)) {
        return prev.filter((t) => t !== layerName);
      } else {
        return [...prev, layerName];
      }
    });
  };

  const handleToggleCategory = (
    category: string,
    checked: boolean | "indeterminate"
  ) => {
    const categoryLayers = layersByCategory[category].map((l) => l.layerName);
    setSelectedLayers((prev) => {
      if (checked === true) {
        // Add all category layers
        const newLayers = [...prev];
        categoryLayers.forEach((layer) => {
          if (!newLayers.includes(layer)) {
            newLayers.push(layer);
          }
        });
        return newLayers;
      } else {
        // Remove all category layers
        return prev.filter((layer) => !categoryLayers.includes(layer));
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      await onSave(selectedLayers);
      setSuccessMessage("Permissões de camadas atualizadas com sucesso!");
    } catch (err: any) {
      console.error("Error saving layers:", err);
      setErrorMessage(err.message || "Erro ao atualizar permissões");
    } finally {
      setSaving(false);
    }
  };

  const isCategoryChecked = (category: string) => {
    const categoryLayers = layersByCategory[category].map((l) => l.layerName);
    return categoryLayers.every((layer) => selectedLayers.includes(layer));
  };

  const isCategoryIndeterminate = (category: string) => {
    const categoryLayers = layersByCategory[category].map((l) => l.layerName);
    const checkedCount = categoryLayers.filter((layer) =>
      selectedLayers.includes(layer)
    ).length;
    return checkedCount > 0 && checkedCount < categoryLayers.length;
  };

  return (
    <div>
      {successMessage && (
        <Alert
          variant="success"
          className="mb-2"
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert
          variant="error"
          className="mb-2"
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Search Bar */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Buscar camadas..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <Search size={20} style={{ marginRight: "8px", color: "#666" }} />
          ),
        }}
      />

      {/* Summary */}
      <Typography variant="body2" className="text-[#676A70] mb-2">
        {selectedLayers.length} de {mockLayers.length} camadas selecionadas
      </Typography>

      {/* Categories */}
      {Object.entries(filteredCategories).map(([category, layers]) => (
        <Accordion
          key={category}
          defaultExpanded={searchTerm.length > 0}
          sx={{
            "&:before": {
              display: "none",
            },
            boxShadow: "none",
            border: "none",
            "&.Mui-expanded": {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ChevronDown size={20} />}
            sx={{
              borderBottom: "none",
              "&.Mui-expanded": {
                minHeight: "48px",
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: "12px 0",
              },
            }}
          >
            <Field orientation="horizontal">
              <StyledCheckbox
                checked={isCategoryChecked(category)}
                onCheckedChange={(checked) =>
                  handleToggleCategory(category, checked)
                }
                onClick={(e) => e.stopPropagation()}
              />
              <Typography variant="subtitle1">{category}</Typography>
            </Field>
          </AccordionSummary>
          <AccordionDetails>
            <FieldGroup className="gap-5">
              {layers.map((layer) => (
                <Field
                  key={layer.layerName}
                  orientation="horizontal"
                  className="ml-1"
                >
                  <StyledCheckbox
                    checked={selectedLayers.includes(layer.layerName)}
                    onCheckedChange={() => handleToggleLayer(layer.layerName)}
                  />

                  <Typography variant="body2">{layer.title}</Typography>
                </Field>
              ))}
            </FieldGroup>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Save Button */}
      <div className="mt-3 flex gap-2">
        <Button onClick={handleSave} disabled={saving || loading}>
          {saving && <CircularProgress size={20} />}
          {saving ? "Salvando..." : "Salvar Alterações"}
        </Button>
        <Button
          variant="outline"
          onClick={() => setSelectedLayers(initialSelected)}
          disabled={saving || loading}
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
}
